import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronRight, Check, X } from 'lucide-react';
import { pricingPlans, BILLING_PERIODS, formatPrice } from '@/config/pricingPlans';
import { trackPricingToggle, trackPricingPrimaryCta, trackPricingDetailsCta } from '@/utils/analytics';
import { useTheme } from '@/contexts/ThemeContext';

const PricingCardsSection = () => {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState(BILLING_PERIODS.MONTHLY);
  const { isDarkMode } = useTheme();

  // Theme-aware colors
  const colors = {
    heading: isDarkMode ? '#ffffff' : '#092036',
    subtext: isDarkMode ? '#e2e8f0' : '#70767C',
    bodyText: isDarkMode ? '#cbd5e1' : '#4B5563',
    sectionBg: isDarkMode ? '#0a1628' : '#ffffff',
    cardBg: isDarkMode ? '#0f2744' : 'white',
    toggleBg: isDarkMode ? '#1e3a5f' : 'white',
    toggleBorder: isDarkMode ? '#2d4a6f' : '#e5e7eb',
    toggleInactiveText: isDarkMode ? '#94a3b8' : '#4b5563',
  };

  const handleToggle = (period) => {
    setBillingPeriod(period);
    trackPricingToggle(period);
  };

  const handlePrimaryCta = (plan) => {
    trackPricingPrimaryCta(plan.id, billingPeriod);
    
    // Build route with billing period for subscriptions
    let route = plan.primaryCta.route;
    if (plan.pricing.type === 'subscription' && route.includes('/checkout')) {
      route += `&billing=${billingPeriod}`;
    }
    navigate(route);
  };

  const handleDetailsCta = (plan) => {
    trackPricingDetailsCta(plan.id);
    navigate(plan.secondaryCta.route);
  };

  const getDisplayPrice = (plan) => {
    if (plan.pricing.type === 'one-time') {
      return {
        prefix: plan.pricing.prefix || null,
        amount: `$${plan.pricing.amount}`,
        label: plan.pricing.label,
        savings: null
      };
    }

    if (plan.pricing.type === 'per-case') {
      return {
        prefix: null,
        amount: `$${plan.pricing.amount}`,
        label: '/case',
        savings: null
      };
    }

    if (plan.pricing.type === 'annual-only') {
      return {
        prefix: null,
        amount: `$${plan.pricing.amount}`,
        label: '/yr',
        savings: null
      };
    }
    
    if (billingPeriod === BILLING_PERIODS.ANNUAL) {
      return {
        prefix: null,
        amount: `$${plan.pricing.annualAmount.toLocaleString()}`,
        label: '/yr',
        savings: plan.pricing.annualSavings
      };
    }
    
    return {
      prefix: null,
      amount: `$${plan.pricing.monthlyAmount}`,
      label: '/mo',
      savings: null
    };
  };

  return (
    <section id="pricing" className="py-20 px-6" style={{ backgroundColor: colors.sectionBg }}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: colors.heading }}>
            All Access Pass
          </h2>
          <p className="text-lg" style={{ color: colors.subtext }}>
            Full library access. One simple fee.
          </p>
        </div>

        {/* Pricing Cards - Only show CE Membership on landing page */}
        <div className="flex justify-center">
          {pricingPlans
            .filter(plan => plan.id !== 'curbside-consult')
            .map((plan) => {
            const IconComponent = plan.icon;
            const priceDisplay = getDisplayPrice(plan);
            
            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden ${
                  plan.highlighted ? 'ring-2 ring-offset-2' : ''
                }`}
                style={{
                  backgroundColor: colors.cardBg,
                  ...(plan.highlighted && { 
                    '--tw-ring-color': plan.color,
                    ringColor: plan.color
                  })
                }}
              >
                {/* Badge */}
                {plan.badge && (
                  <div 
                    className="absolute top-0 left-0 right-0 text-center py-2 text-sm font-semibold text-white"
                    style={{ backgroundColor: plan.badgeColor || plan.color }}
                  >
                    {plan.badge}
                  </div>
                )}

                <div className={`p-8 flex flex-col flex-grow ${plan.badge ? 'pt-14' : ''}`}>
                  {/* Icon */}
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                    style={{ backgroundColor: `${plan.badgeColor || plan.color}15` }}
                  >
                    <IconComponent className="w-7 h-7" style={{ color: plan.badgeColor || plan.color }} />
                  </div>

                  {/* Plan Name */}
                  <h3 className="text-xl font-bold mb-2" style={{ color: colors.heading }}>
                    {plan.name}
                  </h3>

                  {/* Tagline */}
                  <p className="text-sm mb-6" style={{ color: colors.subtext }}>
                    {plan.tagline}
                  </p>

                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      {priceDisplay.prefix && (
                        <span className="text-lg" style={{ color: colors.subtext }}>
                          {priceDisplay.prefix}
                        </span>
                      )}
                      <span className="text-4xl font-bold" style={{ color: colors.heading }}>
                        {priceDisplay.amount}
                      </span>
                      <span className="text-lg" style={{ color: colors.subtext }}>
                        {priceDisplay.label}
                      </span>
                    </div>
                    {priceDisplay.savings && (
                      <p className="text-sm font-medium mt-1" style={{ color: '#16a34a' }}>
                        {priceDisplay.savings}
                      </p>
                    )}
                  </div>

                  {/* Bullets */}
                  <ul className="space-y-3 mb-8 flex-grow">
                    {plan.bullets.map((bullet, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check 
                          className="w-5 h-5 flex-shrink-0 mt-0.5" 
                          style={{ color: plan.badgeColor || plan.color }} 
                        />
                        <span className="text-sm" style={{ color: colors.bodyText }}>
                          {bullet}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTAs */}
                  <div className="space-y-3 mt-auto">
                    {/* Primary CTA */}
                    <Button
                      onClick={() => handlePrimaryCta(plan)}
                      className={`w-full py-6 text-base font-semibold rounded-xl transition-all duration-200 ${
                        plan.highlighted
                          ? 'text-white hover:opacity-90'
                          : 'border-2'
                      }`}
                      style={{
                        ...(plan.highlighted
                          ? { backgroundColor: plan.color }
                          : { 
                              borderColor: isDarkMode ? '#60a5fa' : plan.color, 
                              color: isDarkMode ? '#60a5fa' : plan.color,
                              backgroundColor: isDarkMode ? '#0f2744' : 'white'
                            }
                        )
                      }}
                    >
                      {plan.primaryCta.text}
                      <ChevronRight className="w-5 h-5 ml-1" />
                    </Button>

                    {/* Secondary CTA */}
                    <button
                      onClick={() => handleDetailsCta(plan)}
                      className="w-full text-center text-sm font-medium py-2 transition-all hover:underline"
                      style={{ color: colors.subtext }}
                    >
                      {plan.secondaryCta.text}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* What's Included Comparison Table */}
        <div className="mt-16 max-w-2xl mx-auto">
          <div className="rounded-2xl overflow-hidden shadow-lg" style={{ backgroundColor: colors.cardBg }}>
            {/* Included Section */}
            <div className="p-6 border-b" style={{ borderColor: isDarkMode ? '#1e3a5f' : '#e5e7eb' }}>
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: colors.heading }}>
                <Check className="w-5 h-5 text-green-500" />
                Included with All Access Pass
              </h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <span style={{ color: colors.bodyText }}>All current CE courses</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <span style={{ color: colors.bodyText }}>All future CE courses added during membership</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <span style={{ color: colors.bodyText }}>Certificates and CE tracking</span>
                </li>
              </ul>
            </div>
            
            {/* Not Included Section */}
            <div className="p-6" style={{ backgroundColor: isDarkMode ? '#0a1628' : '#f9fafb' }}>
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: colors.heading }}>
                <X className="w-5 h-5 text-gray-400" />
                Not Included
              </h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: isDarkMode ? '#374151' : '#e5e7eb' }}>
                    <X className="w-4 h-4" style={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }} />
                  </div>
                  <span style={{ color: colors.subtext }}>Curbside Consult access</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: isDarkMode ? '#374151' : '#e5e7eb' }}>
                    <X className="w-4 h-4" style={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }} />
                  </div>
                  <span style={{ color: colors.subtext }}>One-on-one case reviews</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: isDarkMode ? '#374151' : '#e5e7eb' }}>
                    <X className="w-4 h-4" style={{ color: isDarkMode ? '#9ca3af' : '#6b7280' }} />
                  </div>
                  <span style={{ color: colors.subtext }}>Asynchronous Q&A</span>
                </li>
              </ul>
              
              {/* Curbside Consult Upsell */}
              <div className="mt-6 p-4 rounded-lg border" style={{ 
                borderColor: isDarkMode ? '#1e3a5f' : '#e5e7eb',
                backgroundColor: isDarkMode ? '#0f2744' : 'white'
              }}>
                <p className="text-sm mb-2" style={{ color: colors.subtext }}>
                  Want expert case feedback?
                </p>
                <Link 
                  to="/pricing/curbside-consult"
                  className="text-sm font-medium hover:underline inline-flex items-center gap-1"
                  style={{ color: '#FF6B35' }}
                >
                  Learn about Curbside Consult
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Credibility Line */}
        <p className="text-center text-sm mt-12" style={{ color: colors.subtext }}>
          AGD PACE approved provider. Board-certified oral surgeon. 23+ years in private practice.
        </p>
      </div>
    </section>
  );
};

export default PricingCardsSection;
